/**
 * Chart theme configuration — maps design tokens to Recharts + AG Grid.
 * Single source of truth for all chart styling across the platform.
 * v3 — Light mode, navy accent
 */

// ─── Colors ──────────────────────────────────────────────
export const CHART_COLORS = {
  // Primary palette (for data series — ordered for visual distinction)
  series: [
    '#1e3a5f',  // Navy — primary
    '#7c3aed',  // Purple
    '#d97706',  // Amber
    '#059669',  // Emerald
    '#dc2626',  // Red
    '#2563eb',  // Blue
    '#db2777',  // Pink
    '#ea580c',  // Orange
  ],

  // Semantic colors
  positive: '#16a34a',
  negative: '#dc2626',
  neutral: '#64748b',
  accent: '#1e3a5f',

  // Severity
  critical: '#dc2626',
  high: '#d97706',
  medium: '#2563eb',
  low: '#64748b',

  // Backgrounds
  bgBase: '#ffffff',
  bgSurface: '#f4f6f9',
  bgElevated: '#edf0f7',

  // Text
  textPrimary: '#0f172a',
  textSecondary: '#475569',
  textMuted: '#64748b',

  // Grid/axis
  gridLine: 'rgba(0,0,0,0.06)',
  axisLine: 'rgba(0,0,0,0.12)',

  // Tooltip
  tooltipBg: '#ffffff',
  tooltipBorder: '#cbd5e1',
};

// ─── Typography ──────────────────────────────────────────
export const CHART_FONTS = {
  mono: "'Inter', system-ui, -apple-system, sans-serif",
  sans: "'Inter', system-ui, -apple-system, sans-serif",
};

// ─── Recharts default props ──────────────────────────────
export const RECHARTS_DEFAULTS = {
  xAxisProps: {
    stroke: CHART_COLORS.textMuted,
    tick: { fill: CHART_COLORS.textMuted, fontSize: 11, fontFamily: CHART_FONTS.sans },
    tickLine: { stroke: CHART_COLORS.gridLine },
    axisLine: { stroke: CHART_COLORS.axisLine },
  },
  yAxisProps: {
    stroke: CHART_COLORS.textMuted,
    tick: { fill: CHART_COLORS.textMuted, fontSize: 11, fontFamily: CHART_FONTS.mono },
    tickLine: false as const,
    axisLine: false as const,
  },
  gridProps: {
    stroke: CHART_COLORS.gridLine,
    strokeDasharray: '3 3',
    horizontal: true,
    vertical: false,
  },
  tooltipContentStyle: {
    backgroundColor: CHART_COLORS.tooltipBg,
    border: `1px solid ${CHART_COLORS.tooltipBorder}`,
    borderRadius: '10px',
    fontFamily: CHART_FONTS.sans,
    fontSize: '12px',
    color: CHART_COLORS.textPrimary,
    padding: '8px 12px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
  },
  tooltipLabelStyle: {
    color: CHART_COLORS.textSecondary,
    fontFamily: CHART_FONTS.sans,
    fontSize: '11px',
    marginBottom: '4px',
  },
  tooltipCursor: { stroke: CHART_COLORS.accent, strokeWidth: 1, strokeDasharray: '4 4' },
};

// ─── Number formatting ───────────────────────────────────

export function formatCurrency(value: number | null | undefined, compact: boolean = true): string {
  if (value === null || value === undefined || !isFinite(value)) return '\u2014';
  if (compact) {
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`;
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(value);
}

export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !isFinite(value)) return '\u2014';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !isFinite(value)) return '\u2014';
  return new Intl.NumberFormat('en-US').format(value);
}

export function formatDays(value: number | null | undefined): string {
  if (value === null || value === undefined || !isFinite(value)) return '\u2014';
  return `${Math.round(value)}d`;
}

export function formatRatio(value: number | null | undefined): string {
  if (value === null || value === undefined || !isFinite(value)) return '\u2014';
  return `${value.toFixed(1)}x`;
}

export function formatMetricValue(value: number | null | undefined, unit: string): string {
  switch (unit) {
    case 'USD': return formatCurrency(value);
    case '%': return formatPercent(value);
    case 'DAYS': return formatDays(value);
    case 'RATIO': return formatRatio(value);
    case 'COUNT': return formatNumber(value);
    default: return formatCurrency(value);
  }
}

// ─── AG Grid Theme ───────────────────────────────────────
export const AG_GRID_DARK_CLASS = 'ag-theme-alpine';

export const AG_GRID_DEFAULT_COL_DEF = {
  sortable: true,
  resizable: true,
  filter: true,
};

export const AG_GRID_CSS = `
.ag-theme-alpine {
  --ag-background-color: #ffffff;
  --ag-header-background-color: #f4f6f9;
  --ag-row-hover-color: #edf0f7;
  --ag-selected-row-background-color: #e1ecf7;
  --ag-border-color: #d0d7e2;
  --ag-foreground-color: #0f172a;
  --ag-secondary-foreground-color: #475569;
  --ag-font-family: 'Inter', system-ui, sans-serif;
  --ag-font-size: 13px;
  --ag-row-border-color: #edf0f7;
}
.ag-theme-alpine .ag-header-cell-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  font-weight: 500;
}
`;
